import React from 'react';
import T from 'prop-types';
import { filter, uniq } from 'lodash-es';

import SimpleDropdown from '../../../Dropdowns/SimpleDropdown';
import Button from '../../../Buttons/Button';
import Tooltip from '../../../Tooltip';
import CheckboxGroup from '../../../CheckboxGroup';

const growthRates = [
  { text: 'Month-over-Month', value: 'm-over-m' },
  { text: 'Quarter-over-Quarter', value: 'q-over-q' },
  { text: 'Year-over-Year', value: 'y-over-y' },
];

const periods = [
  { label: 'Year-over-Year', value: 'year_over_year' },
  { label: 'Period-over-Period', value: 'period_over_period' },
];

const granularities = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly (fiscal)', value: 'quarterly_f', buttonLabel: 'Qtrly (fiscal)' },
  { label: 'Quarterly (calendar)', value: 'quarterly', buttonLabel: 'Qtrly (calendar)' },
  { label: 'Yearly (fiscal)', value: 'yearly_f', buttonLabel: 'Yrly (fiscal)' },
  { label: 'Yearly (calendar)', value: 'yearly', buttonLabel: 'Yrly (calendar)' },
];

const GrowthRates = ({
  growth_rates,
  widgetType = 'old',
  onChange,
}) => {
  const availableGranularitiesFor = (period, granularity) => {
    const currentPeriodSelectedGranularities = filter(growth_rates, rate => rate.split(':')[0] === period).map(rate => rate.split(':')[1]);
    return filter(granularities, g => g.value === granularity || !currentPeriodSelectedGranularities.includes(g.value));
  };

  const handleChange = (type, value, index) => {
    const copyGrowthRates = growth_rates;
    if (type === 'period') {
      copyGrowthRates[index] = `${value}:${copyGrowthRates[index].split(':')[1]}`;
    } else if (type === 'granularity') {
      copyGrowthRates[index] = `${copyGrowthRates[index].split(':')[0]}:${value}`;
    }
    onChange(uniq(copyGrowthRates));
  };

  const handleAddNewRow = () => {
    if (growth_rates.length === 0) {
      onChange(['year_over_year:quarterly_f']);
    } else if (filter(growth_rates, rate => rate.split(':')[0] === 'year_over_year').length === 6) {
      onChange([...growth_rates, `period_over_period:${availableGranularitiesFor('period_over_period')[0].value}`]);
    } else {
      onChange([...growth_rates, `year_over_year:${availableGranularitiesFor('year_over_year')[0].value}`]);
    }
  };

  const handleRemove = (rate) => {
    onChange(growth_rates.filter(r => r !== rate));
  };

  return (
    <section className="export-settings-section growth-rates-section">
      {widgetType === 'new' && (
        <>
          <div className="export-settings-section__about">
            <h4 className="h5 export-settings-section__title">
              Growth rate
            </h4>
            <p className="paragraph-md export-settings-section__description">Select the comparison type and granularity you would like to use to calculate the growth rate.</p>
            <p className="service-info-text">All growth rates only available as CSV</p>
          </div>
          <div className="export-settings-section__content">
            {growth_rates.map((rate, i) => (
              <div key={rate} className="growth-rates-selection-row">
                <SimpleDropdown
                  options={periods}
                  selected={rate.split(':')[0]}
                  onChange={value => handleChange('period', value, i)}
                />
                <SimpleDropdown
                  options={availableGranularitiesFor(rate.split(':')[0], rate.split(':')[1])}
                  selected={rate.split(':')[1]}
                  onChange={value => handleChange('granularity', value, i)}
                />
                {growth_rates.length >= 1 && (
                  <span className="hicon-close" onClick={() => handleRemove(rate)} />
                )}
              </div>
            ))}
            <Button
              variant="secondary"
              disabled={growth_rates.length === 12}
              onClick={handleAddNewRow}
            >
              Add Growth Rate
            </Button>
          </div>
        </>
      )}
      {widgetType === 'old' && (
        <>
          <div className="export-settings-section__about">
            <h4 className="h5 export-settings-section__title">
              Growth rates
              <Tooltip content="Growth rates will be provided for selected data points in a separate tab">
                <i className="hicon-question-circle" />
              </Tooltip>
            </h4>
            <p className="paragraph-md export-settings-section__description">Depending on your date range, you have the option of choosing how your growth rate is calculated.</p>
          </div>
          <div className="export-settings-section__content">
            <CheckboxGroup
              id="growth-rates-checkboxes"
              active={growth_rates}
              options={growthRates}
              className="export-settings-section__options export-form-checkbox-group growth-rates-options"
              gapCheckbox="8px"
              size="lg"
              onChange={value => onChange(value)}
            />
          </div>
        </>
      )}
    </section>
  );
};

GrowthRates.propTypes = {
  growth_rates: T.array,
  widgetType: T.oneOf(['old', 'new']),
  onChange: T.func.isRequired,
};

export default GrowthRates;
